import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";

import { participantIdAtom, romIdAtom, romPhaseAtom } from "@/atoms/rom.atoms";
import { serverHostAtom, serverPortAtom } from "@/atoms/settings.atoms";
import { romService } from "@/services/rom.service";

/**
 * Listens for `session:disconnect` — user-initiated "leave current host"
 * from the titlebar dropdown. Performs an explicit leave on the backend,
 * clears persisted pairing state, and redirects to /connection-mode so the
 * user can pick another host or stay disconnected.
 *
 * Distinct from `rom:closed` (passive — other side closed the rom) and
 * `auth:session-expired` (auth failure — full logout).
 */
export function useSessionDisconnectListener() {
  const navigate = useNavigate();
  const romId = useAtomValue(romIdAtom);
  const participantId = useAtomValue(participantIdAtom);
  const setRomId = useSetAtom(romIdAtom);
  const setParticipantId = useSetAtom(participantIdAtom);
  const setPhase = useSetAtom(romPhaseAtom);
  const setServerHost = useSetAtom(serverHostAtom);
  const setServerPort = useSetAtom(serverPortAtom);

  useEffect(() => {
    const handler = async () => {
      if (romId && participantId) {
        try {
          await romService.leave(romId, participantId);
        } catch {
          // best-effort; scheduler will reap if this fails
        }
      }

      localStorage.removeItem("@Guest/serverHost");
      localStorage.removeItem("@Guest/serverPort");

      setRomId(null);
      setParticipantId(null);
      setPhase("pairing");
      setServerHost("");
      setServerPort("");

      navigate("/connection-mode", { replace: true });
    };

    window.addEventListener("session:disconnect", handler);
    return () => window.removeEventListener("session:disconnect", handler);
  }, [
    navigate,
    romId,
    participantId,
    setRomId,
    setParticipantId,
    setPhase,
    setServerHost,
    setServerPort,
  ]);
}
